import { map, tap } from "rxjs/operators";
import { inject, injectable } from "tsyringe";

import { autoBind } from "src/shared/decorators/autobind";
import { RewardsGameEndpoints } from "src/containers/wellbeing/rewards/constants/endpoints";
import { ScratchGame } from "src/containers/wellbeing/rewards/domain/models/scratch-game";
import { ScratchGameResult } from "src/containers/wellbeing/rewards/domain/models/scratch-game-result";
import {
    MockGameResultStatus,
    rewardsGameMockResults,
} from "src/containers/wellbeing/rewards/services/scratch-game-service/__mock__/scratch-game-results";
import {
    MockScratchGameStatus,
    rewardsMockScratchGames,
} from "src/containers/wellbeing/rewards/services/scratch-game-service/__mock__/scratch-games";
import { HttpClient } from "src/infra/http/client";
import { Environment } from "src/libs/environment";
import { Log } from "src/libs/log";
import { ScratchGameActions } from "src/containers/wellbeing/rewards/types/scratch";
import type {
    NotifyGameScratchedParams,
    ScratchGameParams,
    SendWinnerDataParams,
} from "src/containers/wellbeing/rewards/types/scratch";
import { PgaPolicy } from "src/shared/models";
import { DayjsUtils } from "src/libs/dayjs-utils";

/**
 * TODO - Remove the mocks
 */
@injectable()
export class ScratchGameService {
    private readonly host: string;
    private readonly apiKey: string;
    private readonly enabled: boolean;

    constructor(@inject("HttpClient") private http: HttpClient) {
        this.host = process.env.REACT_APP_APIMGMT_URL;
        this.apiKey = Environment.string("SCRATCH_GAME_API_KEY");
        this.enabled = Environment.bool("SCRATCH_GAME_ENABLED");
    }

    get userIdentity() {
        return window.userIdentity;
    }

    @autoBind
    getGames(userIdentity: string, useMock = false): Promise<ScratchGame[]> {
        Log.info("Getting all games");

        if (useMock) {
            const statuses = [
                MockScratchGameStatus.NoStock,
                MockScratchGameStatus.UnfinishScratch,
                MockScratchGameStatus.ExceededTries,
                MockScratchGameStatus.PreviouslyWon,
                MockScratchGameStatus.CanPlay,
            ];
            const status =
                statuses[Math.floor(Math.random() * statuses.length)];
            return Promise.resolve([rewardsMockScratchGames.get(status)!]);
        }

        return this.http
            .getJSON<Array<Record<string, any>>>(
                RewardsGameEndpoints.GetAllGames,
                {
                    host: this.host,
                    isPublicRequest: false,
                    customHeader: {
                        ...this.http.httpUtils.getBaseHeaders(),
                        ...this.http.httpUtils.getMsBaseHeaders(
                            undefined,
                            undefined,
                            this.apiKey
                        ),
                    },
                }
            )
            .pipe(
                tap((data) => {
                    return data;
                }),
                map((data) =>
                    data.map((json) => ScratchGame.fromJson<ScratchGame>(json))
                )
            )
            .toPromise();
    }

    @autoBind
    getGame(
        { gameId, userIdentity }: ScratchGameParams,
        useMock = false
    ): Promise<ScratchGameResult> {
        Log.info("Getting game by id: ", gameId);

        if (useMock) {
            const statuses = [
                MockGameResultStatus.Win,
                MockGameResultStatus.Lose,
            ];

            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const status =
                statuses[Math.floor(Math.random() * statuses.length)];
            return Promise.resolve(rewardsGameMockResults.get(status)!);
        }

        return this.http
            .getJSON<Record<string, any>>(
                RewardsGameEndpoints.GetGame.replace(":id", gameId.toString()),
                {
                    host: this.host,
                    isPublicRequest: false,
                    customHeader: {
                        ...this.http.httpUtils.getBaseHeaders(),
                        ...this.http.httpUtils.getMsBaseHeaders(
                            undefined,
                            undefined,
                            this.apiKey
                        ),
                    },
                }
            )
            .pipe(
                map((json) =>
                    ScratchGameResult.fromJson<ScratchGameResult>(json)
                )
            )
            .toPromise();
    }

    notifyGameScratched(payload: NotifyGameScratchedParams): Promise<void> {
        return this.updateGameStatus(payload, ScratchGameActions.Scratched);
    }

    @autoBind
    sendWinnerData(payload: SendWinnerDataParams): Promise<void> {
        return this.updateGameStatus(payload, ScratchGameActions.Notification);
    }

    @autoBind
    updateGameStatus(
        payload: SendWinnerDataParams | NotifyGameScratchedParams,
        action: ScratchGameActions
    ): Promise<void> {
        const { resultId } = payload;
        const url = RewardsGameEndpoints.GameStatus.replace(
            ":id",
            resultId.toString()
        );
        return this.http
            .post<void>(url, {
                host: this.host,
                isPublicRequest: false,
                data: payload,
                params: {
                    action,
                },
                customHeader: {
                    ...this.http.httpUtils.getBaseHeaders(),
                    ...this.http.httpUtils.getMsBaseHeaders(
                        undefined,
                        undefined,
                        this.apiKey
                    ),
                },
            })
            .toPromise();
    }

    public isUserAllowListed(): Promise<boolean> {
        try {
            const shouldUseAllowlist = Environment.bool(
                "SCRATCH_GAME_USE_ALLOWLIST",
                false
            );
            // If allowlist is off then ignore whitelist and return `true`
            if (!shouldUseAllowlist) {
                return Promise.resolve(true);
            }

            return this.http
                .get<boolean>(RewardsGameEndpoints.IsUserAllowed, {
                    host: this.host,
                    isPublicRequest: false,
                    customHeader: {
                        ...this.http.httpUtils.getBaseHeaders(),
                        ...this.http.httpUtils.getMsBaseHeaders(
                            undefined,
                            undefined,
                            this.apiKey
                        ),
                    },
                })
                .toPromise();
        } catch (error) {
            Log.error(error);
            return Promise.resolve(false);
        }
    }

    /**
     * Checks if the user have access to scratch game.
     * There are the conditions:
     *
     * 1. Is running on Flutter or mobile browser.
     * 2. The flag is enabled.
     * 3. There are at least one game available.
     * 4. At least one policy is currently in force.
     *
     * @param policies {Array<PgaPolicy>}
     * @param gamesAvailable {Array<ScratchGame>}
     * @returns {boolean} is the user have access scratch game
     */
    @autoBind
    public hasAccess(
        policies: Array<PgaPolicy>,
        gamesAvailable: Array<ScratchGame>
    ): boolean {
        const isNotFuture =
            policies.length === 0 ? false : policies.every(this.isNotFuture);
        const areGamesAvailable = gamesAvailable.length > 0;
        return this.enabled && areGamesAvailable && isNotFuture;
    }

    @autoBind
    private isNotFuture(policy: PgaPolicy) {
        const validity = DayjsUtils.getDate(policy.validityStart);
        return DayjsUtils.isSameOrBefore(validity, DayjsUtils.today());
    }
}
